import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Card, TextField, Button, Alert, AlertTitle } from "@mui/material";
import TemplatePage from "../TemplatePage";
import { RegisterIPStyle } from "./styles";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import {homepvtKey, homenetwork, compState} from '../../Headerr';
import { broadcast, listUnspent } from '../../webservice/mProviders';
import { DefaultProvider, toHex, bsv, TestWallet } from '../../scrypt-ts-this/distthis/index';
import { useNavigate } from 'react-router-dom';

import IpRegistrationCarousel from "../../components/IpRegistrationCarousel";
import IpRegistrationTutorials from "../../components/IpRegistrationTutorials";

const RegisterIP: FC = () => {
  const { t } = useTranslation();
  const [fileHash, setFileHash] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [author, setAuthor] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isHashing, setIsHashing] = useState<boolean>(false);
  const [txid, setTxid] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const navigate = useNavigate();

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleBoxClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const calculateSHA256 = async (file: File) => {
    setIsHashing(true);
    setTxid("");
    setErrorMsg("");
    try {
      const arrayBuffer = await file.arrayBuffer();
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', arrayBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      setFileHash(hashHex);
    } catch (err) {
      console.error("Erro ao calcular hash:", err);
    } finally {
      setIsHashing(false);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      calculateSHA256(e.dataTransfer.files[0]);
    }
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      calculateSHA256(e.target.files[0]);
    }
  };

  const onRegister = async () => {
    try {
      setErrorMsg("");
      if (homepvtKey.length !== 64) {
        throw new Error(t('registerIP.errNoKey'));
      }

      setIsHashing(true);
      const privateKey = bsv.PrivateKey.fromHex(homepvtKey, homenetwork);
      privateKey.compAdd(compState);
      const changeAddExt = bsv.Address.fromPrivateKey(privateKey);
      
      const provider = new DefaultProvider({network: homenetwork});
      await provider.connect();
      const signer = new TestWallet(privateKey, provider);
      await signer.connect(provider);

      const UTXOs = await listUnspent(changeAddExt, homenetwork);
      if (UTXOs.length === 0) {
        throw new Error(t('registerIP.errNoBalance'));
      }

      // Prepara os dados
      const payloadObj = {
        action: "REGISTER_IP",
        hash: fileHash,
        title,
        author,
        description
      };
      
      const payloadString = JSON.stringify(payloadObj);
      let dataHex = "";
      for (let i = 0; i < payloadString.length; i++) {
        dataHex += payloadString.charCodeAt(i).toString(16).padStart(2, '0');
      }
      const data = dataHex;

      const satsToScript = 1;
      const scriptData = 'OP_DUP OP_HASH160 ' + toHex(changeAddExt.hashBuffer) + ' OP_EQUALVERIFY OP_CHECKSIG ' + data + ' OP_DROP';

      let tx = new bsv.Transaction();
      let tSatoshis = 0;
      for (let i = 0; i < UTXOs.length; i++) {
        tx.from(UTXOs[i]);
        tSatoshis += UTXOs[i].satoshis;
      }

      tSatoshis -= satsToScript;
      tx.addOutput(new bsv.Transaction().Output({
        script: bsv.Script.fromASM(scriptData),
        satoshis: satsToScript,
      }));

      tx.addOutput(new bsv.Transaction().Output({
        script: bsv.Script.buildPublicKeyHashOut(changeAddExt),
        satoshis: tSatoshis,
      }));

      tx = tx.seal().sign(privateKey);
      
      let rawTX = toHex(tx);
      let feeTX = 0;
      if(rawTX.substring(82, 84) === '00') {
          feeTX = Math.floor(((toHex(tx).length/2) - ('00'.length/2) + (tx.DERSEC()[0].length/2))*0.001) + 1;
      } else {
          feeTX = Math.floor((toHex(tx).length/2)*0.001) + 1;
      }

      // Refaz a TX com a taxa
      tx = new bsv.Transaction();
      for (let i = 0; i < UTXOs.length; i++) {
        tx.from(UTXOs[i]);
      }
      
      tx.addOutput(new bsv.Transaction().Output({
        script: bsv.Script.fromASM(scriptData),
        satoshis: satsToScript,
      }));

      if ((tSatoshis - feeTX) > 0) {
        tx.addOutput(new bsv.Transaction().Output({
            script: bsv.Script.buildPublicKeyHashOut(changeAddExt),
            satoshis: tSatoshis - feeTX,
        }));
      }

      tx = tx.seal().sign(privateKey);
      rawTX = toHex(tx);

      if (rawTX.substring(82, 84) === '00') {
          rawTX = rawTX.substring(0, 82) + tx.DERSEC()[0] + rawTX.substring(84, rawTX.length);
      } 

      const resultTxId = await broadcast(rawTX, homenetwork);
      
      if (resultTxId.length === 64) {
        setTxid(resultTxId);
        navigate('/viewtoken/' + resultTxId);
      } else {
        throw new Error(t('registerIP.errBroadcast'));
      }
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || t('registerIP.errUnknown'));
    } finally {
      setIsHashing(false);
    }
  };

  return (
    <TemplatePage>
      <Box sx={RegisterIPStyle.mainContainer}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {t('registerIP.title')}
        </Typography>

        <IpRegistrationCarousel />

        <Alert severity="info" sx={RegisterIPStyle.infoAlert}>
          <AlertTitle>{t('registerIP.howWorks')}</AlertTitle>
          <strong>{t('registerIP.whatIsHash')}</strong> {t('registerIP.whatIsHashText')}
          <br /><br />
          <strong>{t('registerIP.absolutePrivacy')}</strong> {t('registerIP.absolutePrivacyText')}
        </Alert>

        <Card sx={RegisterIPStyle.card}>
          <Typography variant="h6" gutterBottom>{t('registerIP.step1')}</Typography>
          <Box 
            sx={{ ...RegisterIPStyle.dropZone, cursor: 'pointer' }}
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
            onDrop={onDrop}
            onClick={handleBoxClick}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
            <Typography mt={1}>{t('registerIP.dropzoneText')}</Typography>
            <input 
              type="file" 
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={onFileSelect}
            />
          </Box>
          
          {isHashing && <Typography color="textSecondary" mt={2}>{t('registerIP.processing')}</Typography>}
          
          {fileHash && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>{t('registerIP.hashSuccess')}</AlertTitle>
              <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                {fileHash}
              </Typography>
            </Alert>
          )}
        </Card>

        <Card sx={RegisterIPStyle.card}>
          <Typography variant="h6" gutterBottom>{t('registerIP.step2')}</Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            {t('registerIP.step2Desc')}
          </Typography>
          
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField 
              label={t('registerIP.workTitle')}
              variant="outlined" 
              fullWidth 
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTitle(e.target.value)}
            />
            <TextField 
              label={t('registerIP.author')}
              variant="outlined" 
              fullWidth 
              value={author}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setAuthor(e.target.value)}
            />
            <TextField 
              label={t('registerIP.shortDesc')}
              variant="outlined" 
              fullWidth 
              multiline 
              rows={3} 
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDescription(e.target.value)}
            />
          </Box>
        </Card>

        {errorMsg && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>{t('registerIP.errorTitle')}</AlertTitle>
            {errorMsg}
          </Alert>
        )}

        {txid && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>{t('registerIP.successTitle')}</AlertTitle>
            TXID: {txid}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            disabled={!fileHash || isHashing}
            onClick={onRegister}
          >
            {isHashing ? t('registerIP.btnRegistering') : t('registerIP.btnRegister')}
          </Button>
        </Box>
        
        {!fileHash && (
           <Typography variant="caption" color="textSecondary" textAlign="right">
             {t('registerIP.needHash')}
           </Typography>
        )}
        
        <IpRegistrationTutorials />

      </Box>
    </TemplatePage>
  );
};

export default RegisterIP;
